import * as React from "react";
import styled from "styled-components";
import { BasicPlacement } from "tippy.js";
import { Tooltip } from "./Tooltip";
import { useHandleResize } from "./hooks/useHandleResize";

const ChildWrapper = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export type TruncateTextProps = {
  // Since children are passed as 'content' prop to Tooltip (Tippy)
  // when tooltipContent prop is undefined, we match it's prop type
  // https://github.com/atomiks/tippy.js-react/blob/master/index.d.ts#L5
  children: React.ReactChild;
  className?: string;
  distance?: number;
  maxWidth?: number | string;
  placement?: BasicPlacement;
  tooltipContent?: React.ReactChild;
};

export const TruncateText: React.FC<TruncateTextProps> = ({
  children,
  className,
  distance,
  maxWidth,
  placement = "top",
  tooltipContent,
}) => {
  const childRef = React.useRef<{ scrollWidth: number; clientWidth: number }>({ scrollWidth: 0, clientWidth: 0 });

  const [isTruncated, recalculateIsTruncated] = React.useReducer(() => childRef.current.scrollWidth > childRef.current.clientWidth, false);

  React.useLayoutEffect(recalculateIsTruncated, [childRef.current]);

  // It's possible that resizing the window changes whether or not the text
  // is truncated, since set a percentage-based max width
  useHandleResize(recalculateIsTruncated);

  const childContent = (
    <ChildWrapper
      className={className}
      ref={(el) => {
        if (el) {
          childRef.current = el;
        }
      }}
    >
      {children}
    </ChildWrapper>
  );

  // Tooltip content defaults to children (string) but will accept string passed as prop.
  const content = tooltipContent ? tooltipContent : children;

  return isTruncated ? (
    <Tooltip
      arrow={true}
      size="large"
      content={content}
      placement={placement}
      // `boundary` set to 'window' required to prevent undesired behavior
      // when using multiple elements with overflow property set.
      // https://github.com/atomiks/tippyjs/issues/196#issuecomment-371635364
      boundary="window"
      distance={distance}
      maxWidth={maxWidth}
    >
      {childContent}
    </Tooltip>
  ) : (
    childContent
  );
};
